import React, { useState, useEffect, useContext, useCallback } from "react"
import { MultiSelect } from "primereact/multiselect"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Button } from "primereact/button"
import { Card } from "primereact/card"
import { Panel } from "primereact/panel"
import { Dropdown } from "primereact/dropdown"
import { UserContext } from "../../context/userContext"
import { getActivityList } from "../../components/Kiosk/KioskAPi"
import { useLazyQuery } from "@apollo/client"
import { GET_USERS } from "../Housing/queries"
import { Tooltip } from "primereact/tooltip"
import { GET_LOCATIONS_BY_CTX } from "../Locations/queries"
import ROUTES from "../../components/Routes/routesContants"
import { useNavigate } from "react-router-dom"
import styles from "../../styles/kiosk/dashboard/KioskDashboard.module.css"
import { DateTime } from "luxon"

const KioskActivityDashboard = () => {
  const [activities, setActivities] = useState([])
  const [participants, setParticipants] = useState({})
  const [locations, setLocations] = useState([])
  const [selectedParticipants, setSelectedParticipants] = useState([])
  const [timeFrame, setTimeFrame] = useState("hour")
  const [loading, setLoading] = useState(true)
  const [pageSize, setPageSize] = useState(20)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  const [selectedLocation, setSelectedLocation] = useState(null)
  const [locationOptions, setLocationOptions] = useState([])

  const timeFrameOptions = [
    { label: "Last hour", value: "hour" },
    { label: "Last 4 hours", value: "4hours" },
    { label: "Last 8 hours", value: "8hours" },
    { label: "Last 12 hours", value: "12hours" },
    { label: "Last 24 hours", value: "24hours" },
    { label: "Past 7 days", value: "7days" },
    { label: "Last 30 days", value: "30days" },
  ]

  const pageSizeOptions = [
    { label: "10 per page", value: 10 },
    { label: "20 per page", value: 20 },
    { label: "50 per page", value: 50 },
    { label: "100 per page", value: 100 },
  ]

  const userCtx = useContext(UserContext)
  const navigate = useNavigate()

  const { staff_id, organization_id, enterprise_id } = useContext(UserContext)

  const [getUsers] = useLazyQuery(GET_USERS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: staff_id,
      organization_id: organization_id,
    },
  })

  const [getLocations] = useLazyQuery(GET_LOCATIONS_BY_CTX, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: staff_id,
      organization_id: organization_id,
    },
  })

  const getLocationsList = useCallback(async () => {
    let result = await getLocations({
      variables: {
        staff_id: "-6",
        organization_id: organization_id,
      },
    })

    if (result.data && result.data.locations) {
      const formattedLocations = result.data.locations.map((location) => ({
        value: location.location_id,
        label: location.location_name,
      }))
      setLocationOptions(formattedLocations)
      setLocations(result.data.locations)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLocations, enterprise_id])

  const getParticipants = useCallback(async () => {
    let result = await getUsers({
      variables: {
        staff_id: staff_id,
        organization_id: organization_id,
      },
    })

    if (result.data && result.data.participants) {
      const participantsMap = {}
      result.data.participants.forEach((participant) => {
        participantsMap[participant.participant_id] = participant
      })
      setParticipants(participantsMap)
    }
  }, [getUsers, staff_id, organization_id])

  const fetchData = async () => {
    setLoading(true)

    try {
      const currentDateTime = DateTime.now()

      await getParticipants()
      await getLocationsList()
      let startDateTime
      switch (timeFrame) {
        case "hour":
          startDateTime = currentDateTime.minus({ hours: 1 })
          break
        case "4hours":
          startDateTime = currentDateTime.minus({ hours: 4 })
          break
        case "8hours":
          startDateTime = currentDateTime.minus({ hours: 8 })
          break
        case "12hours":
          startDateTime = currentDateTime.minus({ hours: 12 })
          break
        case "24hours":
          startDateTime = currentDateTime.minus({ hours: 24 })
          break
        case "7days":
          startDateTime = currentDateTime.minus({ days: 7 })
          break
        case "30days":
          startDateTime = currentDateTime.minus({ days: 30 })
          break
        default:
          startDateTime = currentDateTime.minus({ hours: 1 })
      }

      const params = {
        organizationId: organization_id,
        startDateTime: startDateTime.toUTC().toISO(),
        endDateTime: currentDateTime.toUTC().toISO(),
        organizationLocationId: selectedLocation,
        participantIds: selectedParticipants,
        pageSize: pageSize,
        pageNum: pageNumber,
      }

      const response = await getActivityList(userCtx.tokenKiosk, params)
      setActivities(response.data)
      setTotalRecords(response.numTotal ?? response.data.length)
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error)
      setActivities([])
      setTotalRecords(0)
      setParticipants({})
      setLocations([])
      setLocationOptions([])
      userCtx?.setNotificationError("Failed to fetch dashboard data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFrame, selectedParticipants, pageSize, pageNumber])

  useEffect(() => {
    getLocationsList()
  }, [getLocationsList])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation])

  const handleLocationChange = (e) => {
    setSelectedLocation(e.value)
  }

  const refresh = () => {
    setPageNumber(1)
    fetchData()
  }

  const header = (
    <div className="p-d-flex p-jc-between p-ai-center">
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}
      >
        <h2 style={{ marginRight: "10px", marginBottom: 0 }}>
          Kiosk Dashboard
        </h2>
        <i
          className="pi pi-info-circle"
          style={{ fontSize: "1.5rem", color: "var(--primary-color)" }}
          data-pr-tooltip="The kiosk dashboard shows participant check-ins using the organizational kiosk up to the last 24 hours. Please use filters to search for the data you are interested in reviewing."
          data-pr-position="right"
          data-pr-at="right+5 top"
          data-pr-my="left center-2"
        />
        <Tooltip target=".pi-info-circle" />
      </div>
      <div className="p-d-flex p-ai-center">
        <MultiSelect
          value={selectedParticipants}
          options={Object.values(participants)
            .sort((a, b) =>
              `${a.participant_name_first} ${a.participant_name_last}`.localeCompare(
                `${b.participant_name_first} ${b.participant_name_last}`
              )
            )
            .map((p) => ({
              label: `${p.participant_name_first} ${p.participant_name_last} (${p.participant_alias})`,
              value: p.participant_id,
            }))}
          onChange={(e) => setSelectedParticipants(e.value)}
          placeholder="Select Participants"
          className="p-mr-2"
          style={{ width: "300px", marginRight: "10px" }}
          filter
          showClear={true}
          resetFilterOnHide={true}
        />
        <Dropdown
          style={{ marginRight: "10px" }}
          value={selectedLocation}
          options={locationOptions}
          showClear={true}
          onChange={handleLocationChange}
          placeholder="Select a Location"
          className="w-full md:w-14rem mr-2"
        />
        <Dropdown
          value={timeFrame}
          options={timeFrameOptions}
          onChange={(e) => setTimeFrame(e.value)}
          placeholder="Select Time Frame"
          className="p-mr-2"
          style={{ width: "200px", marginRight: "10px" }}
        />
        <Dropdown
          value={pageSize}
          options={pageSizeOptions}
          onChange={(e) => setPageSize(e.value)}
          placeholder="Page Size"
          className="p-mr-2"
          style={{ width: "150px", marginRight: "10px" }}
        />
        <Button label="Refresh" icon="pi pi-refresh" onClick={refresh} />
      </div>
    </div>
  )
  const bodyTemplate = (rowData, field) => {
    const isAnonymous = !rowData.participantId || rowData.participantId === "0"

    if (field === "name") {
      if (isAnonymous) {
        return "Anonymous Guest"
      }
      const participant = participants[rowData.participantId]
      if (participant) {
        return (
          <label
            className={styles.participantLink}
            onClick={async () => {
              await userCtx.changeParticipant(participant.participant_id)
              navigate(ROUTES.PARTICIPANT_PROFILE)
            }}
          >
            {`${participant.participant_name_first} ${participant.participant_name_last}`}
          </label>
        )
      }
    } else if (field === "participantAlias") {
      if (isAnonymous) {
        return "Not Provided"
      }
      const participant = participants[rowData.participantId]
      if (participant) {
        return participant.participant_alias || "Not Provided"
      }
    } else if (field === "logInId") {
      if (!rowData.logInId) {
        return "Not Provided"
      }
      const participant = participants[rowData.participantId]
      if (participant) {
        return rowData.logInId
      }
    }

    return rowData[field] || "N/A"
  }

  const dateBodyTemplate = (rowData) => {
    return rowData.date
      ? DateTime.fromISO(rowData.date, { zone: "utc" })
          .setZone("local")
          .toFormat("MM/dd/yyyy")
      : "N/A"
  }

  const timeBodyTemplate = (rowData) => {
    return rowData.date
      ? DateTime.fromISO(rowData.date, { zone: "utc" })
          .setZone("local")
          .toFormat("hh:mm a")
      : "N/A"
  }

  const locationBodyTemplate = (rowData) => {
    const location = locations.find((x) => x.location_id === rowData.locationId)
    return location?.location_name || "N/A"
  }

  const onPage = (event) => {
    setPageNumber(event.page + 1)
    setPageSize(event.rows)
  }

  return (
    <Card>
      <Panel header={header}>
        <DataTable
          value={activities}
          responsiveLayout="scroll"
          loading={loading}
          lazy
          paginator
          first={(pageNumber - 1) * pageSize}
          rows={pageSize}
          totalRecords={totalRecords}
          onPage={onPage}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          rowsPerPageOptions={[10, 20, 50, 100]}
        >
          <Column
            field="name"
            header="Participant Name"
            body={(rowData) => bodyTemplate(rowData, "name")}
            filter={false}
          />
          <Column
            field="participantAlias"
            header="Participant ID"
            body={(rowData) => bodyTemplate(rowData, "participantAlias")}
          />
          <Column
            field="loginId"
            header="Login ID"
            body={(rowData) => bodyTemplate(rowData, "logInId")}
          />
          <Column
            field="locationId"
            header="Location"
            body={locationBodyTemplate}
          />
          <Column field="date" header="Check-in Date" body={dateBodyTemplate} />
          <Column field="time" header="Check-in Time" body={timeBodyTemplate} />
          <Column
            field="activities"
            header="Activities"
            body={(rowData) => bodyTemplate(rowData, "activities")}
          />
        </DataTable>
      </Panel>
    </Card>
  )
}

export default KioskActivityDashboard
